import * as React from 'react';

import type { AiSearchHttpConfig } from '@/imports/ui/components/search/SearchProvider';

import { EMPTY_ASSISTANT_MESSAGE } from '../constants';
import type { AskAssistantMessage, AskUserMessage } from '../types';
import {
  ASK_HTTP_ERROR_DEFAULT_MESSAGE,
  getAskHttpErrorMessage,
} from '../utils/askHttpErrors';
import {
  buildAssistantMessage,
  getAskTransportMode,
  mapApiMode,
  normalizeSessionId,
  payloadFromRecord,
} from '../utils/messageBuilders';

export function useAskQuestion({
  aiSearchHttp,
  userId,
  askSessionId,
  setAskSessionId,
  setAskMessages,
  setAskError,
  setAskLoading,
}: {
  aiSearchHttp: AiSearchHttpConfig | null | undefined;
  userId: string;
  askSessionId: string;
  setAskSessionId: React.Dispatch<React.SetStateAction<string>>;
  setAskMessages: React.Dispatch<
    React.SetStateAction<import('../types').AskMessage[]>
  >;
  setAskError: React.Dispatch<React.SetStateAction<string>>;
  setAskLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const setLastAssistantMessage = React.useCallback(
    (next: AskAssistantMessage) => {
      setAskMessages((prev) => {
        if (!prev.length) return prev;
        const updated = [...prev];
        updated[updated.length - 1] = next;
        return updated;
      });
    },
    [setAskMessages]
  );

  const ask = React.useCallback(
    async (question: string) => {
      const http = aiSearchHttp;
      const q = question.trim();
      if (!q || !http) return false;

      setAskError('');
      setAskLoading(true);
      const userMsg: AskUserMessage = { role: 'user', text: q };
      setAskMessages((prev) => [...prev, userMsg]);

      const askTransportMode = getAskTransportMode();
      const useStream = askTransportMode !== 'json';
      const useStreamTokens = askTransportMode === 'sse_tokens';

      setAskMessages((prev) => [...prev, EMPTY_ASSISTANT_MESSAGE]);

      const url = `${http.baseUrl}/1/indexes/%2A/ai-ask`;
      const body: Record<string, unknown> = {
        question: q,
        stream: useStream,
        ...(useStream ? { streamTokens: useStreamTokens } : {}),
        ...(askSessionId
          ? { sessionId: askSessionId }
          : { startSession: true }),
        ...(userId ? { userId } : {}),
      };

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: http.buildHeaders(),
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          let body: { error?: unknown; message?: unknown } | null = null;
          try {
            const errText = await res.text();
            if (errText.trim()) {
              body = JSON.parse(errText) as {
                error?: unknown;
                message?: unknown;
              };
            }
          } catch {
            body = null;
          }

          const uiMessage = getAskHttpErrorMessage(
            res.status,
            body,
            res.headers.get('Retry-After')
          );
          setLastAssistantMessage({
            ...EMPTY_ASSISTANT_MESSAGE,
            mode: 'error',
            answer: uiMessage,
          });
          return false;
        }

        const contentType = res.headers.get('content-type') || '';
        const isSSE = contentType.includes('text/event-stream');

        if (isSSE && res.body) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let streamedText = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const rawLine = line.slice(6).trim();
              if (!rawLine) continue;
              try {
                const ev = JSON.parse(rawLine) as Record<string, unknown> & {
                  event?: string;
                  text?: string;
                };
                if (ev.event === 'delta' && ev.text != null) {
                  streamedText += String(ev.text);
                  setLastAssistantMessage({
                    ...EMPTY_ASSISTANT_MESSAGE,
                    answer: streamedText,
                  });
                } else if (ev.event === 'final') {
                  const sid = normalizeSessionId(ev);
                  if (sid) {
                    setAskSessionId((prev) => prev || sid);
                  }
                  const modeStr =
                    typeof ev.mode === 'string' ? ev.mode : 'autonomous';
                  setLastAssistantMessage(
                    buildAssistantMessage(
                      ev,
                      mapApiMode(modeStr),
                      (ev.answer as string | null | undefined) ??
                        (streamedText || null)
                    )
                  );
                }
              } catch {}
            }
          }
        } else {
          const rawJson = (await res.json()) as Record<string, unknown>;
          const payload = payloadFromRecord(rawJson);
          const sid = payload.sessionId;
          if (sid) {
            setAskSessionId((prev) => prev || sid);
          }
          const modeStr = payload.mode || 'autonomous';
          setLastAssistantMessage(
            buildAssistantMessage(rawJson, mapApiMode(modeStr))
          );
        }
        return true;
      } catch {
        setLastAssistantMessage({
          ...EMPTY_ASSISTANT_MESSAGE,
          mode: 'error',
          answer: ASK_HTTP_ERROR_DEFAULT_MESSAGE,
        });
        return false;
      } finally {
        setAskLoading(false);
      }
    },
    [
      aiSearchHttp,
      askSessionId,
      setAskError,
      setAskLoading,
      setAskMessages,
      setAskSessionId,
      setLastAssistantMessage,
      userId,
    ]
  );

  return { ask };
}
